/**
 * AdminFactoryPage - M3.1 Async Architecture
 *
 * Daily Factory administration dashboard for the async build pipeline.
 *
 * M3.1 changes:
 * - "Run Daily Factory" creates a job and returns immediately (job_id)
 * - UI polls GET /factory/jobs/:jobId every 2 seconds for status
 * - Progress shows QUEUED -> BUILDING -> TESTING -> REPAIRING -> READY_FOR_APPROVAL
 * - Human Approve button triggers POST /factory/jobs/:jobId/approve
 * - Page refresh restores state from localStorage (last job_id)
 * - History shows recent jobs from the API
 *
 * Route: /admin/factory
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  factoryApi,
  type BuildJobResponse,
  type BuildJobStatusType,
} from '../lib/apiClient';

/** Ordered pipeline steps for the progress display. */
const PIPELINE_STEPS = [
  { key: 'QUEUED', label: 'Queued' },
  { key: 'BUILDING', label: 'Building' },
  { key: 'TESTING', label: 'Testing' },
  { key: 'REPAIRING', label: 'Repairing' },
  { key: 'READY_FOR_APPROVAL', label: 'Ready for Approval' },
] as const;

const ACTIVE_STATUSES: BuildJobStatusType[] = ['QUEUED', 'BUILDING', 'TESTING', 'REPAIRING'];
const FAILED_STATUSES: BuildJobStatusType[] = ['BUILD_FAILED', 'QA_FAILED', 'REJECTED'];

const LAST_JOB_KEY = 'admin_factory_last_job_id';
const POLL_INTERVAL_MS = 2000;

/** Status badge colors. */
function statusBadge(status: BuildJobStatusType): { bg: string; fg: string } {
  switch (status) {
    case 'QUEUED':
      return { bg: '#6b7280', fg: '#ffffff' };
    case 'BUILDING':
      return { bg: '#2563eb', fg: '#ffffff' };
    case 'TESTING':
      return { bg: '#7c3aed', fg: '#ffffff' };
    case 'REPAIRING':
      return { bg: '#d97706', fg: '#ffffff' };
    case 'READY_FOR_APPROVAL':
      return { bg: '#ca8a04', fg: '#ffffff' };
    case 'APPROVED':
      return { bg: '#16a34a', fg: '#ffffff' };
    case 'PUBLISHED':
      return { bg: '#059669', fg: '#ffffff' };
    case 'BUILD_FAILED':
    case 'QA_FAILED':
    case 'REJECTED':
      return { bg: '#dc2626', fg: '#ffffff' };
    default:
      return { bg: '#4b5563', fg: '#ffffff' };
  }
}

function formatDuration(ms: number | undefined): string {
  if (ms === undefined || Number.isNaN(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}

function formatTime(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function isActive(status: BuildJobStatusType): boolean {
  return ACTIVE_STATUSES.includes(status);
}

function isFailed(status: BuildJobStatusType): boolean {
  return FAILED_STATUSES.includes(status);
}

export default function AdminFactoryPage() {
  const [candidateCount, setCandidateCount] = useState<number>(20);
  const [currentJob, setCurrentJob] = useState<BuildJobResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState<boolean>(false);
  const [approving, setApproving] = useState<boolean>(false);
  const [approveMessage, setApproveMessage] = useState<string | null>(null);
  const [confirmingApprove, setConfirmingApprove] = useState<boolean>(false);
  const [recentJobs, setRecentJobs] = useState<BuildJobResponse[]>([]);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Restore last job from localStorage on mount
  useEffect(() => {
    const lastJobId = localStorage.getItem(LAST_JOB_KEY);
    if (lastJobId) {
      void factoryApi.getJob(lastJobId).then(setCurrentJob).catch(() => {
        localStorage.removeItem(LAST_JOB_KEY);
      });
    }
    // Load recent jobs
    void factoryApi.listJobs(undefined, 10).then((res) => setRecentJobs(res.jobs)).catch(() => {});
  }, []);

  // Polling effect
  useEffect(() => {
    if (currentJob && isActive(currentJob.status)) {
      pollRef.current = setInterval(() => {
        if (currentJob) {
          void factoryApi
            .getJob(currentJob.job_id)
            .then((updated) => {
              setCurrentJob(updated);
              if (!isActive(updated.status)) {
                if (pollRef.current) clearInterval(pollRef.current);
                // Refresh recent jobs list
                void factoryApi.listJobs(undefined, 10).then((res) => setRecentJobs(res.jobs)).catch(() => {});
              }
            })
            .catch(() => {});
        }
      }, POLL_INTERVAL_MS);
    }

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [currentJob?.job_id, currentJob?.status]);

  const handleRun = useCallback(async () => {
    setCreating(true);
    setError(null);
    setApproveMessage(null);
    setConfirmingApprove(false);

    try {
      const response = await factoryApi.runDaily(candidateCount);
      setCurrentJob({
        job_id: response.job_id,
        type: response.type,
        status: response.status,
        attempt: 0,
        max_attempts: 3,
        llm_requests: 0,
        llm_tokens: 0,
        repair_count: 0,
        candidate_count: response.candidate_count,
        created_at: response.created_at,
      });
      localStorage.setItem(LAST_JOB_KEY, response.job_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create build job');
    } finally {
      setCreating(false);
    }
  }, [candidateCount]);

  const handleRetry = useCallback(async () => {
    if (!currentJob) return;
    try {
      const retried = await factoryApi.retryJob(currentJob.job_id);
      setCurrentJob(retried);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Retry failed');
    }
  }, [currentJob]);

  const handleRequestApprove = useCallback(() => {
    setConfirmingApprove(true);
    setApproveMessage(null);
  }, []);

  const handleConfirmApprove = useCallback(async () => {
    if (!currentJob) return;
    setApproving(true);
    try {
      const approved = await factoryApi.approveJob(currentJob.job_id);
      setCurrentJob(approved);
      setApproveMessage(`Job approved successfully (game_id: ${approved.game_id ?? 'N/A'}).`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Approval failed');
    } finally {
      setApproving(false);
      setConfirmingApprove(false);
    }
  }, [currentJob]);

  const handleCancelApprove = useCallback(() => {
    setConfirmingApprove(false);
  }, []);

  const handleSelectJob = useCallback((jobId: string) => {
    void factoryApi.getJob(jobId).then(setCurrentJob).catch(() => {});
    localStorage.setItem(LAST_JOB_KEY, jobId);
  }, []);

  const running = currentJob ? isActive(currentJob.status) : false;
  const canRetry = currentJob
    ? isFailed(currentJob.status) && currentJob.attempt < currentJob.max_attempts
    : false;

  return (
    <div style={{ padding: '24px 0' }} data-testid="admin-factory-page">
      {/* Header */}
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h2 style={{ fontSize: '1.5rem', margin: 0 }}>Daily Factory</h2>
          <p style={{ marginTop: 4, fontSize: '0.9rem', color: 'var(--color-text-muted)', maxWidth: 640 }}>
            M3.1 async production pipeline: API creates job &rarr; Queue &rarr; Node Build Worker
            executes &rarr; R2 artifact &rarr; human approval.
          </p>
        </div>
        <Link to="/admin/games" style={{ fontSize: '0.9rem', color: 'var(--color-text-muted)' }}>
          &larr; Game Admin
        </Link>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            marginBottom: 12,
            padding: '10px 14px',
            borderRadius: 'var(--radius)',
            background: 'rgba(220, 38, 38, 0.12)',
            color: '#dc2626',
            fontSize: '0.9rem',
            border: '1px solid rgba(220, 38, 38, 0.4)',
          }}
        >
          <strong>Error.</strong> {error}
        </div>
      )}

      {/* Run Controls */}
      <section
        data-testid="factory-run-controls"
        style={{
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius)',
          padding: 16,
          marginBottom: 16,
          background: 'var(--color-bg-secondary)',
        }}
      >
        <h3 style={{ fontSize: '1.1rem', marginBottom: 12 }}>Run Controls</h3>
        <div
          style={{
            display: 'flex',
            gap: 16,
            alignItems: 'flex-end',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: 4 }}>
              Candidate count
            </label>
            <input
              type="number"
              min={5}
              max={20}
              value={candidateCount}
              disabled={creating || running}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (Number.isNaN(v)) return;
                const clamped = Math.min(20, Math.max(5, v));
                setCandidateCount(clamped);
              }}
              style={{
                width: 120,
                padding: '6px 8px',
                borderRadius: 4,
                border: '1px solid var(--color-border)',
                background: 'var(--color-bg)',
                color: 'var(--color-text)',
              }}
            />
          </div>
          <button
            data-testid="factory-run-btn"
            onClick={() => void handleRun()}
            disabled={creating || running}
            style={{
              padding: '8px 24px',
              borderRadius: 'var(--radius)',
              border: 'none',
              background: creating || running ? '#6b7280' : 'var(--color-primary)',
              color: '#ffffff',
              cursor: creating || running ? 'not-allowed' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: 600,
            }}
          >
            {creating ? 'Creating...' : running ? 'Running...' : 'Run Daily Factory'}
          </button>
          {currentJob && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>Job</span>
              <span
                data-testid="factory-job-status"
                style={{
                  background: statusBadge(currentJob.status).bg,
                  color: statusBadge(currentJob.status).fg,
                  borderRadius: 999,
                  padding: '2px 10px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                }}
              >
                {currentJob.status}
              </span>
            </div>
          )}
        </div>
      </section>

      {/* Current Job Progress */}
      {currentJob && (
        <section
          data-testid="factory-job-progress"
          style={{
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius)',
            padding: 16,
            marginBottom: 16,
            background: 'var(--color-bg-secondary)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ fontSize: '1.1rem', margin: 0 }}>Build Job Progress</h3>
            <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', fontFamily: 'monospace' }}>
              {currentJob.job_id}
            </span>
          </div>

          {/* Step progress bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 16, flexWrap: 'wrap' }}>
            {PIPELINE_STEPS.map((step, idx) => {
              const currentIdx = PIPELINE_STEPS.findIndex((s) => s.key === currentJob.status);
              const isCurrent = step.key === currentJob.status;
              const isPast = currentIdx > idx;
              const isFailedState = isFailed(currentJob.status);
              const bg = isFailedState
                ? '#dc2626'
                : isCurrent
                  ? '#2563eb'
                  : isPast
                    ? '#16a34a'
                    : '#374151';
              return (
                <div key={step.key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div
                    data-testid={`factory-step-${step.key.toLowerCase()}`}
                    style={{
                      padding: '4px 12px',
                      borderRadius: 999,
                      background: bg,
                      color: '#ffffff',
                      fontSize: '0.75rem',
                      fontWeight: isCurrent ? 700 : 500,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {step.label}
                  </div>
                  {idx < PIPELINE_STEPS.length - 1 && (
                    <div style={{ width: 16, height: 2, background: isPast ? '#16a34a' : '#374151' }} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Job metrics */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 12,
              fontSize: '0.85rem',
              marginBottom: 12,
            }}
          >
            <div>
              <div style={{ color: 'var(--color-text-muted)' }}>Elapsed</div>
              <div>{formatDuration(currentJob.elapsed_ms)}</div>
            </div>
            <div>
              <div style={{ color: 'var(--color-text-muted)' }}>Attempt</div>
              <div>{currentJob.attempt} / {currentJob.max_attempts}</div>
            </div>
            <div>
              <div style={{ color: 'var(--color-text-muted)' }}>LLM Requests</div>
              <div>{currentJob.llm_requests}</div>
            </div>
            <div>
              <div style={{ color: 'var(--color-text-muted)' }}>LLM Tokens</div>
              <div>{currentJob.llm_tokens.toLocaleString()}</div>
            </div>
            <div>
              <div style={{ color: 'var(--color-text-muted)' }}>Repairs</div>
              <div>{currentJob.repair_count}</div>
            </div>
            <div>
              <div style={{ color: 'var(--color-text-muted)' }}>Started</div>
              <div>{formatTime(currentJob.started_at)}</div>
            </div>
          </div>

          {/* Error display */}
          {isFailed(currentJob.status) && currentJob.error_message && (
            <div
              role="alert"
              data-testid="factory-job-error"
              style={{
                padding: '10px 14px',
                borderRadius: 'var(--radius)',
                background: 'rgba(220, 38, 38, 0.12)',
                color: '#dc2626',
                fontSize: '0.85rem',
                border: '1px solid rgba(220, 38, 38, 0.4)',
                marginBottom: 12,
              }}
            >
              <strong>{currentJob.error_code ?? 'ERROR'}:</strong> {currentJob.error_message}
            </div>
          )}

          {/* Retry button */}
          {canRetry && (
            <button
              data-testid="factory-retry-btn"
              onClick={() => void handleRetry()}
              style={{
                padding: '6px 16px',
                borderRadius: 'var(--radius)',
                border: '1px solid #d97706',
                background: 'transparent',
                color: '#d97706',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: 600,
              }}
            >
              Retry (attempt {currentJob.attempt + 1}/{currentJob.max_attempts})
            </button>
          )}

          {/* Artifact info */}
          {currentJob.status === 'READY_FOR_APPROVAL' && currentJob.artifact_url && (
            <div
              data-testid="factory-artifact-info"
              style={{
                padding: 12,
                borderRadius: 'var(--radius)',
                background: 'rgba(202, 138, 4, 0.08)',
                border: '1px solid rgba(202, 138, 4, 0.3)',
                marginBottom: 12,
                fontSize: '0.85rem',
              }}
            >
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div><strong>Game ID:</strong> {currentJob.game_id ?? '—'}</div>
                <div><strong>Version:</strong> {currentJob.version ?? '—'}</div>
                <div><strong>Artifact:</strong> <code style={{ fontSize: '0.8rem' }}>{currentJob.artifact_url}</code></div>
                <div><strong>Checksum:</strong> <code style={{ fontSize: '0.8rem' }}>{currentJob.checksum?.substring(0, 16)}…</code></div>
              </div>
            </div>
          )}

          {/* Approval section */}
          {currentJob.status === 'READY_FOR_APPROVAL' && (
            <div data-testid="factory-approval-section">
              {approveMessage && (
                <div
                  role="status"
                  style={{
                    marginTop: 12,
                    padding: '8px 12px',
                    borderRadius: 'var(--radius)',
                    background: 'rgba(5, 150, 105, 0.12)',
                    color: '#059669',
                    fontSize: '0.85rem',
                  }}
                >
                  {approveMessage}
                </div>
              )}
              {confirmingApprove ? (
                <div
                  role="alertdialog"
                  style={{
                    padding: 12,
                    border: '1px solid #ca8a04',
                    borderRadius: 'var(--radius)',
                    background: 'rgba(202, 138, 4, 0.08)',
                  }}
                >
                  <p style={{ fontSize: '0.9rem', marginBottom: 10 }}>
                    Approve this build? This is the human approval gate.
                    Approving does not auto-publish.
                  </p>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      data-testid="factory-confirm-approve-btn"
                      onClick={() => void handleConfirmApprove()}
                      disabled={approving}
                      style={{
                        padding: '6px 16px',
                        borderRadius: 'var(--radius)',
                        border: 'none',
                        background: '#16a34a',
                        color: '#ffffff',
                        cursor: approving ? 'not-allowed' : 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                      }}
                    >
                      {approving ? 'Approving...' : 'Confirm Approve'}
                    </button>
                    <button
                      data-testid="factory-cancel-approve-btn"
                      onClick={handleCancelApprove}
                      disabled={approving}
                      style={{
                        padding: '6px 16px',
                        borderRadius: 'var(--radius)',
                        border: '1px solid var(--color-border)',
                        background: 'transparent',
                        color: 'var(--color-text)',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  data-testid="factory-approve-btn"
                  onClick={handleRequestApprove}
                  disabled={approving}
                  style={{
                    padding: '8px 20px',
                    borderRadius: 'var(--radius)',
                    border: 'none',
                    background: '#16a34a',
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                  }}
                >
                  Approve
                </button>
              )}
            </div>
          )}

          {currentJob.status === 'APPROVED' && (
            <div
              data-testid="factory-approved-message"
              style={{
                padding: '8px 12px',
                borderRadius: 'var(--radius)',
                background: 'rgba(5, 150, 105, 0.12)',
                color: '#059669',
                fontSize: '0.85rem',
              }}
            >
              ✓ Job approved. Game is ready for publishing (manual step in production).
            </div>
          )}
        </section>
      )}

      {/* Recent Jobs */}
      <section
        data-testid="factory-recent-jobs"
        style={{
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius)',
          padding: 16,
          background: 'var(--color-bg-secondary)',
        }}
      >
        <h3 style={{ fontSize: '1.1rem', marginBottom: 12 }}>Recent Build Jobs</h3>
        {recentJobs.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>No jobs yet.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--color-text-muted)' }}>
                  <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-border)' }}>Job ID</th>
                  <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-border)' }}>Status</th>
                  <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-border)' }}>Game</th>
                  <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-border)' }}>Created</th>
                  <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-border)' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {recentJobs.map((job) => (
                  <tr key={job.job_id} data-testid={`factory-job-row-${job.job_id.substring(0, 8)}`}>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-border)', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {job.job_id.substring(0, 8)}…
                    </td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-border)' }}>
                      <span
                        style={{
                          background: statusBadge(job.status).bg,
                          color: statusBadge(job.status).fg,
                          borderRadius: 999,
                          padding: '2px 8px',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                        }}
                      >
                        {job.status}
                      </span>
                    </td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-border)' }}>
                      {job.game_id ? job.game_id.substring(0, 8) + '…' : '—'}
                    </td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-border)' }}>
                      {formatTime(job.created_at)}
                    </td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-border)' }}>
                      <button
                        onClick={() => handleSelectJob(job.job_id)}
                        style={{
                          padding: '2px 8px',
                          borderRadius: 4,
                          border: '1px solid var(--color-border)',
                          background: 'transparent',
                          color: 'var(--color-text)',
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                        }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
